// RootLayout — the root route element. Two jobs:
//
// 1. App-wide providers + cross-cutting concerns that must run inside
//    the router context vite-react-ssg sets up: the TanStack Query
//    provider, the one-time session bootstrap (hydrate), and the API
//    health monitor (auto-redirects to /503 when the backend goes
//    silent mid-deploy).
// 2. Default document head (title, description, Open Graph, Twitter
//    card, JSON-LD). These are the site-wide defaults baked into every
//    pre-rendered route; individual pages override title/description/
//    canonical/og:url via their own <Head>. react-helmet-async keeps
//    the deepest value, so a page's <Head> wins over these defaults.
//
// The site-invariant tags (charset, viewport, theme-color, favicon,
// fonts) stay in index.html — they never change per route and don't
// need to round-trip through React.

import { useEffect } from "react";
import { Outlet } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { Head } from "vite-react-ssg";

import { AppLocaleProvider } from "@/i18n/AppLocaleProvider";
import { useApiHealthMonitor } from "@/hooks/useApiHealthMonitor";
import { queryClient } from "@/lib/queryClient";
import { useAuthStore } from "@/stores/authStore";

const DEFAULT_TITLE =
  "TrueHacking — AI Penetration Testing & Attack Surface Management";
const DEFAULT_DESCRIPTION =
  "TrueHacking is an AI pentest copilot: continuous reconnaissance, external attack surface management, CVSS-scored findings, and realistic attack playbooks — without waiting weeks for a pentest report.";

const ORG_JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://truehacking.ai/#organization",
      name: "TrueHacking",
      url: "https://truehacking.ai/",
      logo: "https://truehacking.ai/logo.png",
      description:
        "AI-assisted offensive security: continuous reconnaissance, external attack surface management, and AI-enriched vulnerability analysis.",
      email: "support@truehacking.ai",
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://truehacking.ai/#software",
      name: "TrueHacking",
      url: "https://truehacking.ai/",
      applicationCategory: "SecurityApplication",
      operatingSystem: "Web",
      publisher: { "@id": "https://truehacking.ai/#organization" },
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free tier — no credit card required.",
      },
    },
  ],
};

// Runs inside both providers so it can use TanStack Query (health
// monitor) and React Router (navigate/location).
function GlobalConcerns() {
  const hydrate = useAuthStore((s) => s.hydrate);
  useApiHealthMonitor();

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  return null;
}

export function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <Head>
        <title>{DEFAULT_TITLE}</title>
        <meta name="description" content={DEFAULT_DESCRIPTION} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TrueHacking" />
        <meta property="og:title" content={DEFAULT_TITLE} />
        <meta property="og:description" content={DEFAULT_DESCRIPTION} />
        <meta
          property="og:image"
          content="https://truehacking.ai/og-cover.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="TrueHacking — AI Pentest Copilot" />

        {/* twitter:title / twitter:description intentionally omitted —
            X/Twitter falls back to the per-route og:title / og:description,
            so the card title stays correct on every page without us
            duplicating it here. */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://truehacking.ai/og-cover.png"
        />

        <script type="application/ld+json">
          {JSON.stringify(ORG_JSON_LD)}
        </script>
      </Head>

      <GlobalConcerns />
      {/* App/auth pages get their locale from the preference store;
          marketing routes nest their own LocaleProvider, which wins. */}
      <AppLocaleProvider>
        <Outlet />
      </AppLocaleProvider>
    </QueryClientProvider>
  );
}
